import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import RadioGroup, { useRadioGroup } from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AirplaneIcon from '@mui/icons-material/AirplanemodeActive';
import CloseIcon from '@mui/icons-material/Close';
import { Form, useNavigation } from '@remix-run/react';

type ShipmentSelectionModalProps = {
  open: boolean;
  onClose: () => void;
  formAction?: string;
  additionalFormInfo?: { name: string; value: string }[];
};

export function ShipmentSelectionModal({ open, onClose, formAction, additionalFormInfo }: ShipmentSelectionModalProps) {
  const navigation = useNavigation();
  const disabled = navigation.state !== 'idle';

  function handleClose() {
    if (disabled) return;
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      sx={{
        zIndex: 1301,
        '& .MuiDialog-paper ': {
          maxWidth: 520,
          py: 3,
          px: 2,
          '& > *': { p: 0 },
          '& > :first-of-type': { pb: 3 },
          '& > :last-of-type': { pt: 3 },
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
          <Stack bgcolor="#F0F5F9" borderRadius="50%" sx={{ p: 1.25 }}>
            <AirplaneIcon color="primary" sx={{ fontSize: 20 }} />
          </Stack>
          <IconButton onClick={handleClose} disabled={disabled} sx={{ p: 0.75 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        What type of shipment do you need?
      </DialogTitle>
      <DialogContent>
        <Form id="shipmentTypeForm" method="post" action={formAction}>
          {additionalFormInfo?.map(({ name, value }) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
          <FormControl fullWidth>
            <RadioGroup name="shipmentType" defaultValue="oneAddress" sx={{ gap: 3 }}>
              <CustomRadio value="oneAddress">
                <Typography component="p" variant="h6">
                  One Address
                </Typography>
                <Typography variant="body1">Ship your order to one address.</Typography>
              </CustomRadio>
              <CustomRadio value="multipleAddresses">
                <Typography component="p" variant="h6">
                  Multiple Address
                </Typography>
                <Typography variant="body1">Select products and ship to multiples recipients.</Typography>
              </CustomRadio>
              <CustomRadio value="warehouse">
                <Typography component="p" variant="h6">
                  Send to Warehouse
                </Typography>
                <Typography variant="body1">Send products to the warehouse.</Typography>
              </CustomRadio>
            </RadioGroup>
          </FormControl>
        </Form>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', gap: 3 }}>
        <Button onClick={handleClose} disabled={disabled} variant="outlined" sx={{ flex: 1 }}>
          CANCEL
        </Button>
        <Button
          disabled={disabled}
          type="submit"
          name="_action"
          value="sendSwag"
          form="shipmentTypeForm"
          variant="contained"
          sx={{ flex: 1 }}
          disableElevation
        >
          CONTINUE
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function CustomRadio({ value, children }: { value: string; children: React.ReactNode }) {
  const radioGroup = useRadioGroup();
  const checked = radioGroup?.value === value;
  return (
    <Stack
      component="label"
      htmlFor={`${radioGroup?.name}-${value}`}
      direction="row"
      alignItems="flex-start"
      p={2}
      gap={1}
      borderRadius={2}
      border="1px solid #EBEBEB"
      width="100%"
      sx={[
        { cursor: 'pointer' },
        checked && { bgcolor: '#F0F5F9', borderColor: ({ palette }) => palette.primary.main },
      ]}
    >
      <Radio id={`${radioGroup?.name}-${value}`} value={value} sx={{ p: 1.25 }} disableRipple />
      <Stack p={0.75}>{children}</Stack>
    </Stack>
  );
}
